import React, { useMemo } from 'react';

import { useLocalSearchParams } from 'expo-router';
import { MenuHeaderNavigation } from 'features/menu/components/menu-header-navigation';
import { MenuItemUnavailable } from 'features/menu/components/menu-item-unavailable';
import MenuTileGrid from 'features/menu/components/menu-tile-grid';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import { MainMenuObject } from 'features/menu/components/menu-tile-grid/menu-tile/types';
import { useFetchMainMenuOnMount } from 'features/menu/hooks/use-fetch-main-menu';
import {
  IMenuObjectWithContentOptions,
  useGetVariantContentOptions,
} from 'features/menu/hooks/use-get-variant-content-options';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { reduceMenuSectionOptions } from 'features/menu/pages/main-menu-page/utils';
import { MenuSectionTabs } from 'features/menu/pages/menu-object-page/menu-section/menu-section-tabs';
import { useFilterSectionOptionsByDayPart } from 'features/menu/pages/menu-object-page/menu-section/use-filter-section-options-by-day-part';
import { useLogMenuContent } from 'features/menu/pages/menu-object-page/use-log-menu-content';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AppTakeoverMenuSectionTransition } from 'components/app-takeover/app-takeover-menu-section-transition';
import { useAppTakeoverMenuSection } from 'components/app-takeover/use-app-takeover';
import { FullWidthAutoHeightImage } from 'components/picture';
import { getStaticPathname } from 'navigation/location/location';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useServiceModeContext } from 'state/service-mode';

// OLD
export const MenuSection = () => {
  const pathname = getStaticPathname();
  const { id } = useLocalSearchParams<{ id: string }>();
  const isMounting = useFetchMainMenuOnMount();

  useMenuScroll();

  const {
    data,
    getPricingAndAvailability,
    getSection,
    isMenuLoading,
    isOffer,
  } = useMainMenuContext();
  const { serviceMode } = useServiceModeContext();

  const filterSectionOptionsByDayPart = useFilterSectionOptionsByDayPart();

  const menuSection = useMemo(() => filterSectionOptionsByDayPart(getSection(id)), [
    filterSectionOptionsByDayPart,
    getSection,
    id,
  ]);

  const { inlineTileSections, standardSections, totalSectionsLength } = useMemo(
    () =>
      reduceMenuSectionOptions({
        menuSectionOptions: (menuSection?.options ?? []) as MainMenuObject[],
        serviceMode,
        getPricingAndAvailability,
      }),
    [getPricingAndAvailability, menuSection?.options, serviceMode]
  );

  useLogMenuContent({
    loggedValues: {
      id: menuSection?._id || '',
      name: menuSection?.name?.locale || '',
      menuType: menuSection?._type || '',
    },
    pathname,
  });

  const getVariantContentOptions = useGetVariantContentOptions();

  const {
    headerBackgroundColor: takeoverHeaderBg,
    headerBackgroundImage,
    footerImage: takeoverFooterImage,
    footerImageOffset: takeoverFooterImageOffset,
    itemBackgroundColor: takeoverBg,
  } = useAppTakeoverMenuSection(id);

  if (isMenuLoading || isMounting) {
    return (
      <>
        <MenuHeaderNavigation title="" />
        <MenuSkeletonLoader />
      </>
    );
  }

  // Review this
  if (!menuSection || totalSectionsLength <= 0) {
    return <MenuItemUnavailable isOffer={isOffer} />;
  }

  const menuSectionName =
    getVariantContentOptions(menuSection as IMenuObjectWithContentOptions).name?.locale ?? '';

  const headerBackgroundColorOverride = headerBackgroundImage
    ? 'transparent'
    : takeoverHeaderBg
    ? takeoverHeaderBg
    : undefined;

  const tabs = (
    <>
      <MenuHeaderNavigation
        title={menuSectionName}
        key={menuSectionName}
        headerBackgroundColorOverride={headerBackgroundColorOverride}
      />
      <MenuSectionTabs
        menuData={data?.Menu}
        menuDataLoading={isMenuLoading}
        headerBackgroundColorOverride={headerBackgroundColorOverride}
      />
    </>
  );

  const takeoverFooterSpacer = takeoverFooterImageOffset && (
    <View style={{ width: '100%', height: takeoverFooterImageOffset }}></View>
  );

  return (
    <View style={{ flex: 1, backgroundColor: takeoverBg }}>
      <AppTakeoverMenuSectionTransition sectionId={id} />
      {headerBackgroundImage ? (
        <View>
          <FullWidthAutoHeightImage source={headerBackgroundImage} />
          <View style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>{tabs}</View>
        </View>
      ) : (
        tabs
      )}
      <RenderStandard id={id} sections={standardSections} footerSpacer={takeoverFooterSpacer} />
      <RenderInlineTileGrid sections={inlineTileSections} />
      {takeoverFooterImage && (
        <View pointerEvents="none" style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
          <FullWidthAutoHeightImage source={takeoverFooterImage} />
        </View>
      )}
    </View>
  );
};

interface IMenuSectionContentTilesProps {
  sections: MainMenuObject[];
  footerSpacer?: React.ReactNode;
}

const RenderInlineTileGrid = React.memo<IMenuSectionContentTilesProps>(({ sections }) => {
  return (
    <>
      {sections.map(section => (
        <MenuTileGrid
          key={section._id}
          // TODO: There is a type mismatch here, this need to be fixed in a future refactor
          // @ts-ignore
          data={section.options || []}
          gridTitle={section.name?.locale}
          objectFitContain={true}
        />
      ))}
    </>
  );
});

const RenderStandard = React.memo<IMenuSectionContentTilesProps & { id: string }>(
  ({ sections, id, footerSpacer }) => {
    const { bottom } = useSafeAreaInsets();
    return sections.length > 0 ? (
      <MenuTileGrid
        key={id}
        data={sections}
        paddingBottom={bottom}
        objectFitContain
        footerSpacer={footerSpacer}
      />
    ) : null;
  }
);
